/**
 * Endgame types and constants for Legendary Arena.
 *
 * Defines the canonical counter key names, escape limit, and result types
 * used by evaluateEndgame to determine victory or loss conditions.
 *
 * All exports are JSON-serializable plain data -- no functions in this file.
 */
/**
 * Canonical counter key names for endgame conditions.
 *
 * Every packet that increments an endgame counter must import and use
 * these constants -- never use the string values directly.
 */
export const ENDGAME_CONDITIONS = {
    ESCAPED_VILLAINS: 'escapedVillains',
    SCHEME_LOSS: 'schemeLoss',
    MASTERMIND_DEFEATED: 'mastermindDefeated',
    // why: WP-732 / D-24553 — the "victory-assured" Mastermind latch, mirroring
    // FINAL_TURN_TRIGGERED. Set to 1 the instant the last Tactic (or, under the
    // optional Final Blow rule, the Mastermind card itself) is defeated. Per
    // Universal Rules v23 §"End of the Game: Players Win", once the Mastermind has
    // no Tactics left the win is ASSURED but the current player still finishes
    // their turn (fighting a few more Villains for VP) — so this latch is NOT a
    // game-ending condition on its own: evaluateEndgame returns null on it and, for
    // the rest of that turn, SUPPRESSES scheme-loss/tie (a deck-out or an Evil-Wins
    // that triggers during the finished turn does not take the win away). It is
    // promoted to the terminal MASTERMIND_DEFEATED at turn.onEnd
    // (promoteMastermindVictoryIfPending), which is the real end-of-game moment.
    MASTERMIND_DEFEATED_PENDING: 'mastermindDefeatedPending',
    // why: WP-367 / D-24159 — the deck-exhaustion "final turn" latch. Set to 1 the
    // moment the Hero Deck or Villain Deck first reaches zero cards, and never
    // cleared thereafter (even if a card effect later refills the deck). This
    // counter is NOT a game-ending condition on its own — it only marks that the
    // current turn is the final turn. evaluateEndgame does NOT end the game on it.
    FINAL_TURN_TRIGGERED: 'finalTurnTriggered',
    // why: WP-367 / D-24159 — the resolved deck-exhaustion tie. Set to 1 by the
    // turn.onEnd hook when FINAL_TURN_TRIGGERED is latched AND no win/loss fired
    // during the final turn. evaluateEndgame reads this to return the 'tie'
    // outcome. Separate from the latch so the game only ends at end of turn, not
    // the instant a deck empties.
    FINAL_TURN_TIE: 'finalTurnTie',
    // why: WP-502 / D-24306 — the player-initiated "End Game" latch. Set to 1 by
    // the endMatchEarly move when the players choose to close out an in-progress
    // match (e.g. they ran out of time). evaluateEndgame reads it FIRST (highest
    // priority) and returns a 'tie' outcome flagged endedEarly, so the top-level
    // endIf sets ctx.gameover for all seats and buildUIState projects the endgame
    // panel to every connected client. Never set at setup — a runtime-only write,
    // so initial G is unchanged (no finalStateHash re-pin).
    MATCH_ENDED_EARLY: 'matchEndedEarly',
};
// why: 8 is the standard Legendary escape limit for MVP; becomes part of
// MatchSetupConfig in a later packet when scheme-specific limits are
// implemented.
/** The number of escaped villains that triggers a scheme-wins loss. */
export const ESCAPE_LIMIT = 8;
//# sourceMappingURL=endgame.types.js.map